import { Component, type OnInit, Inject } from '@angular/core'
import { CommonModule } from '@angular/common'
import { FormGroup, FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms'
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core'
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog'
import { NGXLogger } from 'ngx-logger'
import { CptDateAdapter, TransifexService, CustomerPortalSharedModule } from '@inside-hub-app/customer-portal-shared'
import { SharedService } from '../../../services/shared.service'
import { DatepickerScrollService } from '../../../services/datepickerScroll.service'
import { InstantErrorStateMatcherService } from '../../../services/instant-error-state-matcher.service'
import { HubChildTranslateModule } from '@inside-hub-app/hub-translate'
import { CustomCalendarHeaderComponent } from '../../basic/custom-calendar-header/custom-calendar-header.component'
import {
  type VehicleDTOExtended,
  VehiclesService
} from '../../../services/vehicles.service'
import { add, format } from 'date-fns'
import { APP_DATE_FORMATS } from '../date-picker-formats'

@Component({
  selector: 'customer-portal-app-edit-leasing-expiry',
  standalone: true,
  templateUrl: './edit-leasing-expiry.component.html',
  providers: [
    {
      provide: DateAdapter,
      useClass: CptDateAdapter
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: APP_DATE_FORMATS
    }
  ],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    CustomerPortalSharedModule,
    HubChildTranslateModule
  ]
})
export class EditLeasingExpiryComponent implements OnInit {
  form = new FormGroup({
    date: new FormControl(null)
  })

  loading = false
  private readonly vehicleUpdated: VehicleDTOExtended
  matcher = new InstantErrorStateMatcherService()
  customHeader = CustomCalendarHeaderComponent
  now = new Date()
  fiveYearsAfter = add(new Date(), { years: 5 })
  constructor (
    private readonly dialogRef: MatDialogRef<EditLeasingExpiryComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      vehicle: VehicleDTOExtended
      property: 'leasingExpiryDate' | 'nextServiceDate'
    },
    private readonly vehiclesService: VehiclesService,
    private readonly datepickerScrollService: DatepickerScrollService,
    private readonly logger: NGXLogger,
    public sharedService: SharedService,
    public transifexService: TransifexService
  ) {
    this.vehicleUpdated = data.vehicle
  }

  ngOnInit (): void {
    this.form.get('date').setValue(this.data.vehicle[this.data.property])
  }

  chosenYearHandler (normalizedYear: Date): void {
    const ctrlValue: Date =
      this.form.get('date').value != null
        ? new Date(this.form.get('date').value)
        : new Date()
    ctrlValue.setFullYear(normalizedYear.getFullYear())

    this.form.get('date').setValue(ctrlValue)
  }

  chosenMonthHandler (normalizedMonth: Date, picker): void {
    const ctrlValue: Date =
      this.form.get('date').value != null
        ? new Date(this.form.get('date').value)
        : new Date()
    ctrlValue.setMonth(normalizedMonth.getMonth())

    this.form.get('date').setValue(ctrlValue)
    picker.close()
  }

  update (): void {
    this.loading = true
    const dateValue = this.form.get('date').value
    const formattedDate = dateValue == null ? null : format(new Date(dateValue), 'yyyy-MM-dd')

    // Both fields always sent; only the edited one changes, the other keeps its current value
    const details = {
      nextServiceDate: this.data.property === 'nextServiceDate'
        ? formattedDate
        : this.vehicleUpdated.nextServiceDate ?? null,
      leasingExpiryDate: this.data.property === 'leasingExpiryDate'
        ? formattedDate
        : this.vehicleUpdated.leasingExpiryDate ?? null
    }
    // legacy fallback still needs the full vehicle (built immutably, no mutation)
    const legacyVehicle = {
      ...this.sharedService.removeExtraVehicleProperties(this.vehicleUpdated),
      [this.data.property]: formattedDate
    }

    this.vehiclesService.updateOwnershipDetails(legacyVehicle, details).subscribe({
      next: () => {
        this.loading = false
        this.dialogRef.close({ success: true, value: formattedDate })
      },
      error: error => {
        this.loading = false
        this.logger.debug(error)
        this.dialogRef.close({ success: false })
      }
    })
  }

  addScrolling (picker): void {
    this.datepickerScrollService.addScrolling(picker)
  }
}
